import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuthenticatedFetch, getGravityUserId, getGravityUserOrgId } from '../../../hooks/useAuth';
import { useAuthStore } from '../../../stores/authStore';
import type {
  AtlasAccessContext,
  AtlasAccessSummary,
  AtlasAction,
  AtlasActionState,
} from '../models/AtlasAccessSummary';

export const ATLAS_ACCESS_STALE_TIME_MS = 5 * 60 * 1000;
export const ATLAS_ACCESS_GC_TIME_MS = 15 * 60 * 1000;
const UNAVAILABLE: AtlasActionState = { state: 'unavailable', retryable: true };

export function atlasAccessSummaryKey(input: {
  principalId: string | null;
  actorOrgId: string | null;
  folderId: string;
  diagramId: string;
  context?: AtlasAccessContext;
}) {
  return [
    'atlas',
    'access',
    input.principalId ?? 'anonymous',
    input.actorOrgId ?? 'unknown-org',
    input.folderId,
    input.diagramId,
    input.context?.kind ?? 'target',
    input.context?.kind === 'record'
      ? input.context.recordId
      : input.context?.kind === 'scenario' ? input.context.changeSetId : '',
  ] as const;
}

export function buildAtlasAccessSummaryUrl(input: {
  folderId: string;
  diagramId: string;
  context?: AtlasAccessContext;
}): string {
  const base = `/api/folders/${encodeURIComponent(input.folderId)}/diagrams/${encodeURIComponent(input.diagramId)}/atlas/access`;
  if (!input.context) return base;
  const query = new URLSearchParams({ contextKind: input.context.kind });
  if (input.context.kind === 'record') query.set('recordId', input.context.recordId);
  else query.set('changeSetId', input.context.changeSetId);
  return `${base}?${query}`;
}

export function useAtlasAccessSummary(input: {
  folderId?: string | null;
  diagramId?: string | null;
  context?: AtlasAccessContext;
  enabled?: boolean;
}) {
  const folderId = input.folderId ?? '';
  const diagramId = input.diagramId ?? '';
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authenticatedFetch = useAuthenticatedFetch();
  const principalId = getGravityUserId();
  const actorOrgId = getGravityUserOrgId();
  const queryEnabled = Boolean(
    (input.enabled ?? true) && isAuthenticated && principalId && actorOrgId && folderId && diagramId,
  );
  const query = useQuery({
    queryKey: atlasAccessSummaryKey({ principalId, actorOrgId, folderId, diagramId, context: input.context }),
    enabled: queryEnabled,
    staleTime: ATLAS_ACCESS_STALE_TIME_MS,
    gcTime: ATLAS_ACCESS_GC_TIME_MS,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<AtlasAccessSummary> => {
      const response = await authenticatedFetch(buildAtlasAccessSummaryUrl({ folderId, diagramId, context: input.context }), {
        cache: 'no-store',
      });
      if (!response.ok) throw new AtlasAccessSummaryHttpError(response.status);
      return response.json() as Promise<AtlasAccessSummary>;
    },
  });
  const failClosed = queryEnabled && (!query.data || query.isError || query.isPending);
  const actionState = useMemo(() => (action: AtlasAction): AtlasActionState => (
    !queryEnabled ? { state: 'unsupported' } : failClosed ? UNAVAILABLE : query.data!.actions[action]
  ), [failClosed, query.data, queryEnabled]);
  return { ...query, summary: failClosed ? null : query.data, actionState };
}

export class AtlasAccessSummaryHttpError extends Error {
  constructor(readonly status: number) {
    super(`Atlas access summary failed (${status})`);
  }
}
